import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Target, Rocket, Star } from 'lucide-react';

const DOKLevelsSection = () => {
  const levels = [
    {
      level: 1,
      title: "Recall & Reproduction",
      description: "Basic recall of facts, terms, and simple procedures",
      icon: <Brain className="w-8 h-8 text-primary" />,
      examples: ["List", "Define", "Identify", "Recall"]
    },
    {
      level: 2,
      title: "Skills & Concepts",
      description: "Use of information and conceptual knowledge",
      icon: <Target className="w-8 h-8 text-primary" />,
      examples: ["Compare", "Classify", "Organize", "Summarize"]
    },
    {
      level: 3,
      title: "Strategic Thinking",
      description: "Reasoning, planning, and using evidence",
      icon: <Rocket className="w-8 h-8 text-primary" />,
      examples: ["Analyze", "Evaluate", "Create", "Design"]
    },
    {
      level: 4,
      title: "Extended Thinking",
      description: "Complex reasoning, planning, and investigation",
      icon: <Star className="w-8 h-8 text-primary" />,
      examples: ["Synthesize", "Design", "Develop", "Create"]
    }
  ];

  return (
    <section id="dok-levels" className="py-20 bg-secondary/10">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-primary mb-4">
            Depth of Knowledge Levels
          </h2>
          <p className="text-xl text-primary/80 max-w-3xl mx-auto">
            A framework that helps teachers assess the level of thinking a student demonstrates on a task, from recalling facts to analyzing, connecting, or applying ideas in real-world contexts.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {levels.map((level, index) => (
            <motion.div
              key={level.level}
              className="bg-cream rounded-2xl p-8 border border-secondary/30"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-primary/10 rounded-full p-3">
                  {level.icon}
                </div>
                <div>
                  <div className="text-sm font-medium text-primary/60">Level {level.level}</div>
                  <h3 className="text-xl font-semibold text-primary">{level.title}</h3>
                </div>
              </div>
              <p className="text-primary/80 mb-6">{level.description}</p>
              <div className="space-y-2">
                {level.examples.map((example, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary/40"></div>
                    <span className="text-primary/70">{example}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DOKLevelsSection;
